"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
const serviceItems = [{
  id: "01",
  title: "Interior Design",
  description: "We establish the architectural framework of each area by refining how the space flows, functions and feels. From there, we develop the detailed drawings and documentation that guide execution, remaining involved through construction to help preserve the integrity of the design.",
  deliverables: "Concept development, space planning, custom millwork design and finish selections"
}, {
  id: "02",
  title: "3D Architectural Renderings",
  description: "We transform design ideas into immersive visual experiences through photorealistic architectural renderings, product imagery and advanced 3D visualization.",
  deliverables: "Architectural Visualization · Photorealistic Interiors + Exteriors · Material + Finish Exploration · Lighting + Atmosphere · Product Visualization · Presentation Imagery"
}, {
  id: "03",
  title: "Graphic Design & Branding",
  description: "Creating brands with clarity, character and lasting relevance. We combine research, strategy, identity design and website design to build distinctive brands, from logos and visual identity to digital experiences and broader brand direction, helping businesses establish credibility, create new opportunities and grow with purpose over time.",
  deliverables: "Brand Strategy · Naming + Messaging · Logo Design · Visual Identity · Color + Typography Systems · Brand Guidelines  · Website Design · Website Development · Digital Brand Experience · Marketing Collateral · Presentation + Social Media Assets"
}];
export default function Services() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const shouldReduceMotion = useReducedMotion();
  const toggleItem = (index: number) => {
    setExpandedIndex(prev => prev === index ? null : index);
  };
  return <section id="serviceslist" data-nav="light" className="bg-background text-foreground border-b border-border ![padding:0px_0px_128px_0px]">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column (5 Cols) */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <p className="text-[11px] font-sans uppercase tracking-[0.22em] text-muted-foreground mb-6">
                Services
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal leading-tight tracking-tight text-foreground mb-6">Every stage, thoughtfully considered.</h2>
              <p className="text-base sm:text-lg font-sans font-light leading-relaxed text-muted-foreground max-w-md">We lead clients through a clear, considered design journey, from the first conversation through final completion</p>
            </div>

            <div className="mt-10 pt-8 border-t border-border/70">
              <div className="flex items-center gap-4 text-xs tracking-wider uppercase text-muted-foreground">
                <span className="inline-block w-2 h-2 rounded-full bg-foreground/40" />
                <span>BESPOKE RESIDENTIAL + COMMERCIAL INTERIORS & ARCHITECTURE
</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column (7 Cols) - Accordion */}
          <div className="lg:col-span-7">
            <div className="border-t border-border">
              {serviceItems.map((item, index) => {
              const isOpen = expandedIndex === index;
              return <div key={item.id} data-index={index} className="border-b border-border transition-colors duration-200">
                    <Button variant="ghost" onClick={() => toggleItem(index)} className="w-full flex items-start sm:items-center justify-between py-7 px-0 h-auto hover:bg-transparent rounded-none text-left group whitespace-normal" aria-expanded={isOpen}>
                      <div className="flex items-baseline gap-4 md:gap-6 pr-4">
                        <span className="text-xs font-mono text-muted-foreground/80 tracking-wider">
                          {item.id}
                        </span>
                        <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-normal tracking-tight text-foreground transition-colors group-hover:text-foreground/75">
                          {item.title}
                        </h3>
                      </div>
                      <div className="shrink-0 size-8 flex items-center justify-center border border-border rounded-none text-muted-foreground group-hover:border-foreground group-hover:text-foreground transition-colors mt-1 sm:mt-0">
                        {isOpen ? <Minus className="size-4" /> : <Plus className="size-4" />}
                      </div>
                    </Button>

                    <AnimatePresence initial={false}>
                      {isOpen && <motion.div initial={shouldReduceMotion ? {
                    opacity: 1,
                    height: "auto"
                  } : {
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: "auto"
                  }} exit={shouldReduceMotion ? {
                    opacity: 0,
                    height: 0
                  } : {
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.35,
                    ease: [0.16, 1, 0.3, 1]
                  }} className="overflow-hidden">
                          <div className="pb-8 pt-2 pl-0 sm:pl-10 max-w-2xl">
                            <p className="text-base sm:text-lg font-sans font-light leading-relaxed text-muted-foreground mb-6">
                              {item.description}
                            </p>
                            <div className="pt-4 border-t border-border/50 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4 text-xs">
                              <span className="uppercase tracking-widest text-foreground/80 font-medium shrink-0">
                                Scope:
                              </span>
                              <span className="text-muted-foreground font-light leading-normal">
                                {item.deliverables}
                              </span>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}